import React from "react";

import { DeleteForeverOutlined, Add } from "@mui/icons-material";
import { FormHelperText, IconButton, TextField, Button } from "@mui/material";
import styled from "styled-components";

export interface ReStreamDestination {
    id: string;
    url: string;
}

interface Props {
    destinations: ReStreamDestination[];
    onChange: (destinations: ReStreamDestination[]) => void;
}

const Destinations = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const DestinationEntry = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;
    gap: 10px;
`;

const FieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 10px;
`;

const FieldRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    width: 100%;
`;

const AddButton = styled(Button)`
    margin-top: 10px;
    align-self: flex-start;
`;

export default function ChannelRestreamDestinations ({ destinations, onChange }: Props) {
    const ALLOWED_PROTOCOLS = ["rtmp:", "rtmps:", "srt:"];

    const isValidUrl = (url: string): boolean => {
        if (url === "") return false;

        try {
            const urlObj = new URL(url);
            return ALLOWED_PROTOCOLS.includes(urlObj.protocol);
        } catch {
            return false;
        }
    };

    const isValidId = (id: string): boolean => {
        return id.trim().length > 0;
    };

    const handleAddDestination = () => {
        onChange([...destinations, { id: "", url: "" }]);
    };

    const handleRemoveDestination = (index: number) => {
        onChange(destinations.filter((_, i) => i !== index));
    };
    const handleChangeDestination = (index: number, field: keyof ReStreamDestination, value: string) => {
        onChange(destinations.map((dest, i) => (i === index ? { ...dest, [field]: value } : dest)));
    };

    return (
        <Destinations>
            {destinations.map((destination, index) => {
                const hasValidUrl = isValidUrl(destination.url);
                const hasValidId = isValidId(destination.id);

                return (
                    <div key={`destination-entry-${index}`} style={{ marginBottom: "20px" }}>
                        <DestinationEntry>
                            <FieldsContainer>
                                <FieldRow>
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        label='ID'
                                        value={destination.id}
                                        error={!hasValidId && destination.id !== ""}
                                        onChange={e => handleChangeDestination(index, "id", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        label='URL'
                                        value={destination.url}
                                        error={!hasValidUrl}
                                        onChange={e => handleChangeDestination(index, "url", e.target.value)}
                                    />
                                </FieldRow>

                                {!hasValidId && destination.id !== "" && (
                                    <FormHelperText error>Destination ID is required</FormHelperText>
                                )}

                                {!hasValidUrl && destination.url !== "" && (
                                    <FormHelperText error>
                                        Please enter a valid URL with protocol: {ALLOWED_PROTOCOLS.join(", ")}
                                    </FormHelperText>
                                )}
                            </FieldsContainer>

                            <IconButton
                                color='error'
                                onClick={() => handleRemoveDestination(index)}
                                style={{ marginTop: "8px" }}
                            >
                                <DeleteForeverOutlined />
                            </IconButton>
                        </DestinationEntry>
                    </div>
                );
            })}

            <AddButton variant='contained' startIcon={<Add />} onClick={handleAddDestination}>
                Add Destination
            </AddButton>
        </Destinations>
    );
}
